import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, ChevronDown, ChevronUp, ArrowRight, Rocket } from "lucide-react";
import { onboardingApi, onboardingPlansApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import OnboardingPlansModal from "./OnboardingPlansModal";

const BANNER_BG = "#1B66B4";
const BODY_BG = "#A2D2FD";
const TEXT_COLOR = "#353434";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

/**
 * Onboarding checklist banner shown to company admins on the desktop Dashboard.
 * Collapsed by default; expands to a sectioned accordion. Auto-hides once every
 * item across every section is complete (no manual dismiss).
 */
export default function OnboardingChecklist() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [openSections, setOpenSections] = useState(null);
  const [onboardingPlans, setOnboardingPlans] = useState([]);
  const [plansModalOpen, setPlansModalOpen] = useState(false);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    onboardingApi
      .checklist()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    onboardingPlansApi
      .list()
      .then((p) => {
        if (!cancelled) setOnboardingPlans(p || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const firstIncompleteIdx = useMemo(() => {
    if (!data) return 0;
    const idx = data.sections.findIndex((s) => s.items.some((i) => !i.done));
    return idx === -1 ? 0 : idx;
  }, [data]);

  useEffect(() => {
    if (data && openSections === null) {
      setOpenSections(new Set([firstIncompleteIdx]));
    }
  }, [data, firstIncompleteIdx, openSections]);

  const toggleSection = (idx) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  if (!isAdmin || loading || !data) return null;
  if (data.allDone) return null;

  const pct = data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0;

  return (
    <div data-testid="onboarding-checklist" className="rounded-xl overflow-hidden shadow-sm">
      <div
        className="w-full px-5 py-4 flex items-center justify-between gap-3"
        style={{ backgroundColor: BANNER_BG, color: "#FFFFFF" }}
      >
        <button
          type="button"
          data-testid="onboarding-toggle-btn"
          onClick={() => setExpanded((e) => !e)}
          className="flex items-center gap-3 min-w-0 flex-1 text-left"
        >
          <div className="h-10 w-10 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Rocket className="h-5 w-5 text-white" strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-white leading-tight" data-testid="onboarding-title">
              Get your workspace ready
            </h3>
            <p className="text-[12.5px] text-white/85 mt-0.5" data-testid="onboarding-progress-label">
              {data.completed} of {data.total} steps complete ({pct}%) — finish setup to start winning jobs.
            </p>
          </div>
        </button>
        <div className="flex items-center gap-2.5 shrink-0">
          {onboardingPlans.length > 0 && (
            <button
              type="button"
              data-testid="done-for-you-onboarding-btn"
              onClick={() => setPlansModalOpen(true)}
              className="h-8 px-3.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-[12.5px] font-semibold whitespace-nowrap"
            >
              Done for You Onboarding
            </button>
          )}
          <button
            type="button"
            data-testid="onboarding-chevron-btn"
            onClick={() => setExpanded((e) => !e)}
          >
            {expanded ? (
              <ChevronUp className="h-5 w-5 text-white shrink-0" data-testid="onboarding-collapse-icon" />
            ) : (
              <ChevronDown className="h-5 w-5 text-white shrink-0" data-testid="onboarding-expand-icon" />
            )}
          </button>
        </div>
      </div>

      <OnboardingPlansModal open={plansModalOpen} onClose={() => setPlansModalOpen(false)} />

      {expanded && (
        <div style={{ backgroundColor: BODY_BG, color: TEXT_COLOR }}>
          <div className="h-1.5 bg-white/40">
            <div
              className="h-full transition-all duration-500"
              style={{ width: `${pct}%`, backgroundColor: BANNER_BG }}
              data-testid="onboarding-progress"
            />
          </div>

          <div className="px-4 py-3 space-y-2">
            {data.sections.map((section, idx) => {
              const sectionDone = section.items.filter((i) => i.done).length;
              const sectionTotal = section.items.length;
              const isOpen = openSections?.has(idx);
              const slug = slugify(section.title);
              return (
                <div
                  key={section.title}
                  data-testid={`onboarding-section-${slug}`}
                  className="rounded-lg overflow-hidden bg-white/40"
                >
                  <button
                    type="button"
                    data-testid={`onboarding-section-toggle-${slug}`}
                    onClick={() => toggleSection(idx)}
                    className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left"
                    style={{ color: TEXT_COLOR }}
                  >
                    <span className="font-semibold text-[14px]">{section.title}</span>
                    <span className="flex items-center gap-2">
                      <span className="text-[12px] font-medium opacity-80">
                        {sectionDone}/{sectionTotal}
                      </span>
                      {isOpen ? (
                        <ChevronUp className="h-4 w-4" style={{ color: TEXT_COLOR }} />
                      ) : (
                        <ChevronDown className="h-4 w-4" style={{ color: TEXT_COLOR }} />
                      )}
                    </span>
                  </button>

                  {isOpen && (
                    <ul className="divide-y divide-black/10 bg-white/30">
                      {section.items.map((item) => (
                        <li key={item.key} data-testid={`onboarding-item-${item.key}`}>
                          <Link
                            to={item.href}
                            className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/40 transition-colors"
                          >
                            {item.done ? (
                              <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                            ) : (
                              <Circle className="h-5 w-5 shrink-0 opacity-40" style={{ color: TEXT_COLOR }} />
                            )}
                            <div className="flex-1 min-w-0">
                              <div
                                className={`text-[13.5px] font-semibold ${item.done ? "line-through opacity-60" : ""}`}
                                style={{ color: TEXT_COLOR }}
                              >
                                {item.label}
                              </div>
                            </div>
                            {!item.done && (
                              <ArrowRight className="h-4 w-4 shrink-0" style={{ color: BANNER_BG }} />
                            )}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
